/**
 * SCROLL SPY CUSTOM ELEMENT
 * Tracks a list of anchor links against the sections they point at, and marks the
 * link for the section currently filling the view with data-ui="visible".
 *
 * For set up:
 *   1. Wrap the anchor links with <x-scroll-spy>.
 *   2. Give each <a> the 'group' class.
 *   3. Use 'group-data-visible:' on the label for stateful styling.
 *
 * Links whose hash has no matching element in the document are left untouched,
 * so server-rendered active states on non-anchor links still apply.
 */

interface SpyEntry {
  link: HTMLAnchorElement
  target: Element
}

export class ScrollSpyElement extends HTMLElement {
  /**
   * How much of the viewport a section must cover before it takes the highlight
   * from the section already holding it. Sitting below this keeps the current
   * link marked, so the highlight doesn't flip back and forth mid-scroll.
   */
  static COVERAGE_RATIO = 0.65

  private entries: SpyEntry[] = []
  private observer: IntersectionObserver | undefined
  private onscreen = new Set<Element>()
  private active: Element | undefined
  private ticking = false
  private frame: number | null = null

  private handleObserve = (records: IntersectionObserverEntry[]) => {
    records.forEach((record) => {
      if (record.isIntersecting) this.onscreen.add(record.target)
      else this.onscreen.delete(record.target)
    })
    this.updateActiveLink()
  }

  private handleScroll = () => {
    if (this.ticking) return
    this.ticking = true
    this.frame = window.requestAnimationFrame(() => {
      this.frame = null
      this.updateActiveLink()
      this.ticking = false
    })
  }

  connectedCallback() {
    this.entries = this.collectEntries()
    if (!this.entries.length) return

    this.observer = new IntersectionObserver(this.handleObserve)
    this.entries.forEach(({ target }) => this.observer?.observe(target))

    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.handleScroll, { passive: true })
  }

  disconnectedCallback() {
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame)
      this.frame = null
    }
    this.ticking = false

    this.observer?.disconnect()
    this.observer = undefined
    this.onscreen.clear()

    this.entries = []
    this.active = undefined

    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleScroll)
  }

  private get viewportTop(): number {
    const height = getComputedStyle(document.documentElement).getPropertyValue('--height-header-adapt')
    return parseFloat(height) || 0
  }

  private collectEntries(): SpyEntry[] {
    const links = Array.from(this.querySelectorAll<HTMLAnchorElement>('a[href*="#"]'))

    return links
      .reduce<SpyEntry[]>((entries, link) => {
        const id = link.hash.slice(1)
        if (!id) return entries

        const target = document.getElementById(decodeURIComponent(id))
        if (target) entries.push({ link, target })
        return entries
      }, [])
      .sort(({ target: a }, { target: b }) =>
        a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
      )
  }

  /**
   * How much of the visible area a section occupies, from 0 to 1. Measured against
   * the section's own height so short sections can still score a full 1, and capped
   * to the visible area so sections taller than the view aren't penalised.
   */
  private coverage(target: Element, viewportTop: number, viewportBottom: number): number {
    const area = viewportBottom - viewportTop
    if (area <= 0) return 0

    const { top, bottom, height } = target.getBoundingClientRect()
    const visible = Math.min(bottom, viewportBottom) - Math.max(top, viewportTop)
    if (visible <= 0) return 0

    return visible / Math.min(height, area)
  }

  /**
   * The section covering most of the view wins, ties going to the topmost section.
   * A new winner only takes over once it clears COVERAGE_RATIO, except on first
   * run where the best candidate is used so a link is marked straight away.
   */
  private updateActiveLink() {
    const viewportTop = this.viewportTop
    const viewportBottom = window.innerHeight

    const best = this.entries.reduce<{ target: Element; coverage: number } | undefined>((winner, { target }) => {
      if (!this.onscreen.has(target)) return winner
      const coverage = this.coverage(target, viewportTop, viewportBottom)
      if (!coverage || (winner && coverage <= winner.coverage)) return winner
      return { target, coverage }
    }, undefined)

    if (best && (!this.active || best.coverage >= ScrollSpyElement.COVERAGE_RATIO)) {
      this.active = best.target
    }

    this.entries.forEach(({ link, target }) => {
      if (target === this.active) link.dataset.ui = 'visible'
      else delete link.dataset.ui
    })
  }
}
